import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "app/components/container";
import { useResourcesContext, useEmpresa } from "app/contexts";
import { filterSpaces, rtnRegex } from "app/utils";
import { useDate } from "app/hooks";
import { Row } from "app/components/row";
import { Column } from "app/components/column";
import { DFlexResponsive } from "app/components/d-flex-responsive";
import { Button } from "app/components/button";
import { Input } from "app/components/input";
import { Select } from "app/components/select";
import { Option } from "app/components/option";
import { CardBody } from "app/components/card-body";
import { CardFooter } from "app/components/card-footer";

const initialDepto = {
  departamento_id: 0,
  ciudades: [],
};

const initialCity = {
  ciudad_id: 0,
  ciudad_matricula_requerida: false,
  ciudad_inscripcion_requerida: true,
  ciudad_tomo_requerido: true,
};

export function EmpresaForm() {
  const navigate = useNavigate();

  const {
    denominacionesSociales,
    representacionesGeograficas,
    departamentos,
    getDepartamento,
    getDepartamentoByCiudadId,
    getCiudad,
    camarasDeComercio,
    decretosLegislativos,
  } = useResourcesContext();

  const { empresa, setEmpresa } = useEmpresa();

  const [departamento, setDepartamento] = useState(initialDepto);
  const [ciudad, setCiudad] = useState(initialCity);

  const { MAX_DATE } = useDate();

  useEffect(() => {
    try {
      if (!!empresa.registro_mercantil_ciudad_id) {
        const departamento = getDepartamentoByCiudadId(
          empresa.registro_mercantil_ciudad_id
        );

        const ciudad = getCiudad(empresa.registro_mercantil_ciudad_id);

        setDepartamento(departamento);
        setCiudad(ciudad);
      } else {
        setDepartamento(initialDepto);
        setCiudad(initialCity);
      }
    } catch (error) {
      console.log(error);
    }
  }, [
    empresa.registro_mercantil_ciudad_id,
    getDepartamentoByCiudadId,
    getCiudad,
  ]);

  function handleSubmit(e) {
    e.preventDefault();

    return navigate("../establecimientos");
  }

  return (
    <Container>
      <Row>
        <Column>
          <form className="card" onSubmit={handleSubmit}>
            <CardBody>
              <Container>
                <Row>
                  {/* información general */}
                  <Column xl={6} xxl={6}>
                    <Container>
                      <h5>
                        <strong>Datos Generales</strong>
                      </h5>

                      <small className="form-text text-muted">
                        Información general de la empresa
                      </small>

                      <Row>
                        <Column>
                          <Select
                            placeholder="Denominación social"
                            value={empresa.denominacion_social_id}
                            onChange={(e) =>
                              setEmpresa((data) => ({
                                ...data,
                                denominacion_social_id: parseInt(
                                  e.target.value
                                ),
                              }))
                            }
                          >
                            {denominacionesSociales.map((denominacion) => (
                              <Option
                                key={denominacion.denominacion_social_id}
                                value={denominacion.denominacion_social_id}
                                title={`${
                                  denominacion.denominacion_social_nombre
                                } ${
                                  !!denominacion.denominacion_social_abreviatura
                                    ? `(${denominacion.denominacion_social_abreviatura})`
                                    : ""
                                }`}
                              />
                            ))}
                          </Select>
                        </Column>

                        <Column>
                          <Input
                            placeholder="Nombre comercial"
                            value={empresa.empresa_nombre_comercial}
                            onChange={(e) =>
                              setEmpresa((empresa) => {
                                return {
                                  ...empresa,
                                  empresa_nombre_comercial: filterSpaces(
                                    e.target.value,
                                    /\s{2,}/
                                  ),
                                };
                              })
                            }
                          />
                        </Column>

                        <Column>
                          <Input
                            placeholder="RTN de la empresa, propietario o representante legal"
                            value={empresa.empresa_rtn}
                            onChange={(e) =>
                              setEmpresa((empresa) => ({
                                ...empresa,
                                empresa_rtn: filterSpaces(e.target.value),
                              }))
                            }
                            pattern={rtnRegex.source}
                            title="Ej. 01019000123456"
                          />
                        </Column>

                        <Column xl={6} xxl={6}>
                          <Select
                            placeholder="Representación geográfica"
                            value={empresa.representacion_geografica_id}
                            onChange={(e) =>
                              setEmpresa((data) => ({
                                ...data,
                                representacion_geografica_id: parseInt(
                                  e.target.value
                                ),
                              }))
                            }
                          >
                            {representacionesGeograficas.map(
                              (representacion) => (
                                <Option
                                  key={
                                    representacion.representacion_geografica_id
                                  }
                                  value={
                                    representacion.representacion_geografica_id
                                  }
                                  title={
                                    representacion.representacion_geografica_nombre
                                  }
                                />
                              )
                            )}
                          </Select>
                        </Column>

                        <Column xl={6} xxl={6}>
                          <Input
                            type="number"
                            placeholder="Cantidad de empleados"
                            value={empresa.empresa_cantidad_de_empleados || ""}
                            onChange={(e) =>
                              setEmpresa((empresa) => ({
                                ...empresa,
                                empresa_cantidad_de_empleados:
                                  e.target.valueAsNumber,
                              }))
                            }
                            min={1}
                          />
                        </Column>

                        <Column>
                          <Select
                            placeholder="¿La empresa está registrada en otra Cámara de Comercio?"
                            value={empresa.camara_de_comercio_id}
                            onChange={(e) =>
                              setEmpresa((data) => ({
                                ...data,
                                camara_de_comercio_id: parseInt(e.target.value),
                              }))
                            }
                            helpText="En el caso de que la empresa está registrada en otra Cámara de Comercio"
                          >
                            {camarasDeComercio.map((camara) => (
                              <Option
                                key={camara.camara_de_comercio_id}
                                value={camara.camara_de_comercio_id}
                                title={camara.camara_de_comercio_nombre}
                              />
                            ))}
                          </Select>
                        </Column>

                        <Column>
                          <Select
                            placeholder="¿La empresa está adherida a algún Decreto Legislativo?"
                            value={empresa.decreto_legislativo_id}
                            onChange={(e) => {
                              setEmpresa((empresa) => ({
                                ...empresa,
                                decreto_legislativo_id: parseInt(
                                  e.target.value
                                ),
                              }));
                            }}
                            helpText="En el caso de que la empresa es beneficiaria de algún Decreto Legislativo"
                          >
                            {decretosLegislativos.map((decreto) => (
                              <Option
                                key={decreto.decreto_legislativo_id}
                                value={decreto.decreto_legislativo_id}
                                title={`${decreto.decreto_legislativo_numero} ${
                                  decreto.decreto_legislativo_numero
                                    ? " - "
                                    : ""
                                } ${decreto.decreto_legislativo_nombre}`}
                              />
                            ))}
                          </Select>
                        </Column>
                      </Row>
                    </Container>
                  </Column>

                  {/* Registro mercantil */}
                  <Column xl={6} xxl={6}>
                    <Container>
                      <h5>
                        <strong>Registro Mercantil</strong>
                      </h5>

                      <small className="form-text text-muted">
                        Información del sello del Registro Mercantil del
                        Instituto de la Propiedad
                      </small>

                      <Row>
                        <Column xl={6} xxl={6}>
                          <Select
                            placeholder="Departamento"
                            value={departamento.departamento_id}
                            onChange={(e) => {
                              const departamento = getDepartamento(
                                parseInt(e.target.value)
                              );

                              setDepartamento(departamento);
                              setCiudad(initialCity);
                              setEmpresa((empresa) => ({
                                ...empresa,
                                registro_mercantil_ciudad_id: 0,
                              }));
                            }}
                          >
                            {departamentos.map((departamento) => (
                              <Option
                                key={departamento.departamento_id}
                                value={departamento.departamento_id}
                                title={departamento.departamento_nombre}
                              />
                            ))}
                          </Select>
                        </Column>

                        <Column xl={6} xxl={6}>
                          <Select
                            placeholder="Municipio"
                            value={empresa.registro_mercantil_ciudad_id}
                            onChange={(e) =>
                              setEmpresa((empresa) => ({
                                ...empresa,
                                registro_mercantil_ciudad_id: parseInt(
                                  e.target.value
                                ),
                              }))
                            }
                            disabled={!departamento.departamento_id}
                          >
                            {departamento.ciudades.map((ciudad) => (
                              <Option
                                key={ciudad.ciudad_id}
                                value={ciudad.ciudad_id}
                                title={ciudad.ciudad_nombre}
                              />
                            ))}
                          </Select>
                        </Column>

                        <Column xl={6} xxl={6}>
                          <Input
                            type="date"
                            placeholder="Fecha"
                            value={empresa.registro_mercantil_fecha}
                            onChange={(e) =>
                              setEmpresa((empresa) => ({
                                ...empresa,
                                registro_mercantil_fecha: e.target.value,
                              }))
                            }
                            max={MAX_DATE}
                            min="1900-01-01"
                          />
                        </Column>

                        <Column xl={6} xxl={6}>
                          <Input
                            placeholder={`Matricula ${
                              !!ciudad.ciudad_matricula_requerida
                                ? "(requerida)"
                                : "(opcional)"
                            }`}
                            value={empresa.registro_mercantil_matricula}
                            onChange={(e) =>
                              setEmpresa((empresa) => ({
                                ...empresa,
                                registro_mercantil_matricula: filterSpaces(
                                  e.target.value
                                ),
                              }))
                            }
                            required={!!ciudad.ciudad_matricula_requerida}
                          />
                        </Column>

                        <Column xl={6} xxl={6}>
                          <Input
                            placeholder={`No. de inscripción (${
                              !!ciudad.ciudad_inscripcion_requerida
                                ? "requerida"
                                : "opcional"
                            })`}
                            value={empresa.registro_mercantil_inscripcion}
                            onChange={(e) =>
                              setEmpresa((empresa) => ({
                                ...empresa,
                                registro_mercantil_inscripcion: filterSpaces(
                                  e.target.value
                                ),
                              }))
                            }
                            required={!!ciudad.ciudad_inscripcion_requerida}
                          />
                        </Column>

                        <Column xl={6} xxl={6}>
                          <Input
                            placeholder={`No. de tomo (${
                              !!ciudad.ciudad_tomo_requerido
                                ? "requerido"
                                : "opcional"
                            })`}
                            value={empresa.registro_mercantil_tomo}
                            onChange={(e) =>
                              setEmpresa((empresa) => ({
                                ...empresa,
                                registro_mercantil_tomo: filterSpaces(
                                  e.target.value
                                ),
                              }))
                            }
                            required={!!ciudad.ciudad_tomo_requerido}
                          />
                        </Column>

                        <Column>
                          <Input
                            type="number"
                            placeholder="Capital mínimo autorizado en Lempiras"
                            value={
                              empresa.registro_mercantil_capital_autorizado
                            }
                            onChange={(e) =>
                              setEmpresa((empresa) => ({
                                ...empresa,
                                registro_mercantil_capital_autorizado:
                                  e.target.valueAsNumber,
                              }))
                            }
                            min={0}
                            helpText="Si no declara capital, por favor, ingrese 0"
                          />
                        </Column>
                      </Row>
                    </Container>
                  </Column>
                </Row>
              </Container>
            </CardBody>

            <CardFooter>
              <DFlexResponsive justifyContent="end">
                <Button type="submit" title="Siguiente" color="success" />
              </DFlexResponsive>
            </CardFooter>
          </form>
        </Column>
      </Row>
    </Container>
  );
}
